import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { isoToZoneParts } from "@/components/psychologist/calendar/timeZoneHelpers";
import type { OccupyingSession } from "@/pages/psychologist/tabs/comunidad/formacionBlock";
import { calendarPath } from "@/routes/slugs";

export interface HolidaySessionsInfo {
  /** YYYY-MM-DD, día del feriado en la zona horaria de la psicóloga. */
  date: string;
  name: string;
  sessions: OccupyingSession[];
}

interface Props {
  info: HolidaySessionsInfo | null;
  timezone: string;
  onClose: () => void;
}

/**
 * "Tenés sesiones agendadas ese día": por qué no se puede bloquear un feriado con sesiones,
 * con la lista de pacientes y horarios (en su zona horaria) y la política de cancelación /
 * reprogramación. Lo usan "Bloquear mi agenda" del aviso de feriado en Comunidad y el aviso de
 * feriados de la Agenda. No cancela ni modifica ninguna sesión.
 */
export const HolidaySessionsDialog = ({ info, timezone, onClose }: Props) => {
  const navigate = useNavigate();
  return (
    <Dialog open={info !== null} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="flex flex-col gap-4 p-6">
        <DialogHeader className="mb-0">
          <DialogTitle className="m-0 text-[#2D2049]">Tenés sesiones agendadas ese día</DialogTitle>
        </DialogHeader>
        {info && (
          <>
            <p className="m-0 text-sm leading-relaxed text-[#4A3D73]">
              Todavía no podés bloquear el {info.date.split("-").reverse().slice(0, 2).join("/")} (
              {info.name}) porque tenés {info.sessions.length === 1 ? "esta sesión" : "estas sesiones"}:
            </p>
            <ul className="m-0 flex list-none flex-col gap-1.5 rounded-xl bg-[#F5F1FF] p-3">
              {info.sessions.map((sesion) => (
                <li key={sesion.startIso} className="flex justify-between gap-3 text-sm text-[#2D2049]">
                  <span className="font-semibold">{sesion.patientName || "Paciente"}</span>
                  <span className="shrink-0">{isoToZoneParts(sesion.startIso, timezone).time} hs</span>
                </li>
              ))}
            </ul>
            <ul className="m-0 flex list-disc flex-col gap-1 pl-5 text-[13px] leading-relaxed text-[#4A3D73]">
              <li>
                Antes de cancelar o reprogramar, avisale a tu paciente y esperá su respuesta. Te recomendamos hacerlo
                con al menos 24 h de anticipación.
              </li>
              <li>
                Con menos de 6 h de anticipación solo se puede reprogramar por fuerza mayor. Si no, corresponde
                ofrecerle una sesión gratuita en compensación.
              </li>
              <li>Cuando ya no tengas sesiones ese día, vas a poder bloquearlo desde acá.</li>
            </ul>
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                variant="outline"
                className="border-[#C9BCE8] text-[#665090]"
                onClick={() => {
                  onClose();
                  navigate(`${calendarPath("day")}?date=${info.date}`);
                }}
              >
                Ver en mi Agenda
              </Button>
              <Button className="bg-[#665090] text-white hover:bg-[#665090]/90" onClick={onClose}>
                Entendido
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default HolidaySessionsDialog;
